/**
 * The bundled agent-registry FLOOR (ADR-0060) — data only.
 *
 * Moved here from `apps/edge/src/identity/registry.ts` by ADR-0229 so the
 * edge matcher and the origin's observation linking (crawl-event,
 * agent_observed) read ONE list. The edge re-exports it unchanged as
 * `BUNDLED_REGISTRY`; ADR-0060 semantics are unchanged: these entries are
 * canonical / read-only, KV can only ADD ids that are not listed here.
 *
 * Every pattern here is in-tree and ReDoS-safe by review: a literal token
 * anchored with `\b`, then either a bounded `{1,64}` version segment or a
 * word boundary. No nested or unbounded quantifiers. `agent-registry.test`
 * in packages/types pins that shape for every entry.
 *
 * Adding an entry: first-party documentation of the UA token is required
 * (`docs_url`). Robots.txt-only tokens (no UA of their own) are marked
 * `robots_txt_only` — the matcher skips them.
 */
// Version-segment shape used by the bundled regexes. Permissive enough to
// match documented version strings AND vendor-internal release tags that
// don't follow strict semver — e.g. ChatGPT-User/webprod-20260601 in 2026.
// Bounded `{1,64}` for ReDoS safety (no unbounded `+`/`*`). Allowed chars:
// alphanumerics, dot, hyphen, underscore — covers semver, date-stamps, and
// pre-release tags without admitting arbitrary garbage.
const V = '[A-Za-z0-9._-]{1,64}';
// ADR-0232 — forward-confirmed reverse DNS suffixes, each from the
// operator's own verification page (checked 2026-10-07):
//   Google  https://developers.google.com/crawling/docs/crawlers-fetchers/verify-google-requests
//           — common crawlers (Googlebot, -Image, -Video,
//           Google-InspectionTool) resolve to crawl-*.googlebot.com /
//           geo-crawl-*.geo.googlebot.com, so ONLY `googlebot.com` is
//           accepted for them. `google.com` is NOT accepted (security
//           review B1): user-triggered fetchers resolve to
//           google-proxy-*.google.com and would verify as Googlebot;
//           special-case crawlers (rate-limited-proxy-*.google.com) are
//           not registered. `googleusercontent.com` is NOT accepted either:
//           Google Cloud customer VMs get forward-confirmed
//           `*.bc.googleusercontent.com` names.
//   Bing    https://blogs.bing.com/webmaster/2012/08/31/how-to-verify-that-bingbot-is-bingbot
//           — `search.msn.com`.
//   Apple   https://support.apple.com/en-us/119829 — `applebot.apple.com`.
const GOOGLE_COMMON_CRAWLER_RDNS = ['googlebot.com'];
// Word-boundary anchor (\b) instead of start-anchor (^). Many AI agent UAs
// are Mozilla-wrapped — e.g. `Mozilla/5.0 ... (compatible; bingbot/2.0; ...)`
// or `Mozilla/5.0 ... (Applebot-Extended/0.1; ...)` — so a start-anchor
// would miss them. The word boundary still prevents partial-name
// false positives like `MyChatGPT-User/...` because the char before
// the prefix must be a non-word char (space, paren, semicolon, …) or
// the start of string.
//
// Identification is informational, not authoritative — a malicious agent
// can claim any UA. Phase 1 surfaces who the agent says it is; Phase 2
// requires ed25519-signed mandates for actual authorization.
const BUNDLED_PUBLIC_AND_FIXTURES = [
    // -- Anthropic ---------------------------------------------------------
    {
        agent_id: 'agent_anthropic_claude',
        category: 'ai_crawler',
        organization: 'Anthropic',
        // ClaudeBot is Anthropic's documented training crawler.
        // https://docs.claude.com/en/docs/agents/web-crawlers
        user_agent_pattern: `\\bClaudeBot/${V}`,
        sec_ch_ua_sill_agent: '"ClaudeBot"',
        // 2026-10-11 seed audit: Anthropic's help article says "If a crawler
        // has a source IP address on this list, it indicates that the crawler
        // is coming from Anthropic", linking https://claude.com/crawling/bots.json
        // (Google-style `prefixes[].ipv4Prefix`, verified 2026-10-11).
        //   https://support.claude.com/en/articles/8896518
        // Anthropic states the list positively ("on this list -> Anthropic"),
        // not as complete, so there is no `ip_list_complete`: an off-list
        // address stays Unconfirmed (security review B1).
        ip_ranges: [{ url: 'https://claude.com/crawling/bots.json', format: 'google_json' }],
        public_keys: [],
    },
    {
        agent_id: 'agent_anthropic_claude_user',
        category: 'ai_assistant',
        organization: 'Anthropic',
        // Claude's user-facing browse / web_fetch tool UA. Distinct from
        // ClaudeBot which is the indexing crawler — fires on user-initiated
        // fetches, not bulk training. Claude Code's fetch sends
        // `Claude-User (claude-code/<version>; +https://support.anthropic.com/)`
        // with no slash after the token, so both forms match.
        user_agent_pattern: `\\bClaude-User(?:/${V}| \\(claude-code/${V})`,
        sec_ch_ua_sill_agent: '"Claude-User"',
        // 2026-10-18 — Anthropic's published list https://claude.com/crawling/bots.json
        // is the SAME file ClaudeBot cites. Claude's server-side `web_fetch`
        // (the browse feature on claude.ai) sources from that list and WILL
        // forward-confirm. But Claude Code and other Claude-powered agents
        // run the fetch from the end user's own machine — a residential IP
        // that is OFF the list by design. Mode `confirm_only` wires this up
        // cleanly: in-range → `published_ip` (Verified, same as ClaudeBot);
        // out-of-range → `unverified` with NO impersonation reason and NO
        // `ip_off_list` evidence (never a Mismatch, never feeds
        // `crawler_name_impersonation`, never blocks). This is the only
        // reason the seed carries a list at all.
        //   https://support.claude.com/en/articles/8896518
        ip_ranges: [{ url: 'https://claude.com/crawling/bots.json', format: 'google_json' }],
        ip_ranges_mode: 'confirm_only',
        public_keys: [],
    },
    // ADR-0159 addition — grade-A vendor-documented search-quality bot,
    // the third of Anthropic's three-token roster. Additive-only per
    // ADR-0060 (new `agent_id`, no rename, no removal, canary triple
    // unchanged).
    //   https://support.anthropic.com/en/articles/8896518
    {
        agent_id: 'agent_anthropic_claude_searchbot',
        category: 'ai_crawler',
        organization: 'Anthropic',
        user_agent_pattern: `\\bClaude-SearchBot/${V}`,
        sec_ch_ua_sill_agent: '"Claude-SearchBot"',
        // Same Anthropic list as ClaudeBot (see above). NOT applied to
        // Claude-User: Claude Code's fetch sends that token from the user's
        // own machine, which would read as off-list.
        ip_ranges: [{ url: 'https://claude.com/crawling/bots.json', format: 'google_json' }],
        public_keys: [],
    },
    // -- OpenAI ------------------------------------------------------------
    {
        agent_id: 'agent_openai_gpt',
        category: 'ai_crawler',
        organization: 'OpenAI',
        // GPTBot is OpenAI's documented training crawler.
        // https://platform.openai.com/docs/bots
        user_agent_pattern: `\\bGPTBot/${V}`,
        sec_ch_ua_sill_agent: '"GPTBot"',
        ip_ranges: [{ url: 'https://openai.com/gptbot.json', format: 'google_json' }],
        docs_url: 'https://developers.openai.com/api/docs/bots',
        public_keys: [],
    },
    {
        agent_id: 'agent_openai_chatgpt_user',
        category: 'ai_assistant',
        organization: 'OpenAI',
        // ChatGPT-User is sent when a ChatGPT user invokes the browse tool
        // (interactive web fetch on behalf of an end user). Real-world UAs
        // include non-numeric versions like `ChatGPT-User/webprod-20260601`.
        user_agent_pattern: `\\bChatGPT-User/${V}`,
        sec_ch_ua_sill_agent: '"ChatGPT-User"',
        ip_ranges: [{ url: 'https://openai.com/chatgpt-user.json', format: 'google_json' }],
        // ADR-0214 §5 — OpenAI signs agent-mode requests as chatgpt.com.
        web_bot_auth_signers: ['chatgpt.com'],
        docs_url: 'https://developers.openai.com/api/docs/bots',
        public_keys: [],
    },
    {
        agent_id: 'agent_openai_chatgpt',
        category: 'ai_assistant',
        organization: 'OpenAI',
        // ChatGPT/X.X — the ChatGPT mobile app's in-app browser / fetch UA
        // (distinct from ChatGPT-User which is the web-browse tool). Added
        // 2026-06-01 after a user reported their iOS/Android ChatGPT
        // visits classified as UNKNOWN AGENT despite carrying a clear
        // ChatGPT/ token. Anchored by \b so this doesn't false-positive on
        // an unrelated token containing "ChatGPT" in any other position.
        //
        // NOTE: this does NOT detect ChatGPT Atlas (the OpenAI browser /
        // host for ChatGPT Agent). Atlas uses the standard Chrome UA with
        // no public, cryptographically signed identity — OpenAI's docs
        // explicitly state Atlas "cannot be reliably detected with simple
        // user agent filters." Atlas visits will appear as HUMAN-LIKELY
        // and there is no way to distinguish them from a real Chrome
        // user without an AP2 / agent-card signing handshake (Phase 2).
        user_agent_pattern: `\\bChatGPT/${V}`,
        sec_ch_ua_sill_agent: '"ChatGPT"',
        public_keys: [],
    },
    {
        agent_id: 'agent_openai_searchbot',
        category: 'ai_crawler',
        organization: 'OpenAI',
        // OAI-SearchBot is the ChatGPT Search crawler (separate from GPTBot
        // training crawler).
        user_agent_pattern: `\\bOAI-SearchBot/${V}`,
        sec_ch_ua_sill_agent: '"OAI-SearchBot"',
        ip_ranges: [{ url: 'https://openai.com/searchbot.json', format: 'google_json' }],
        docs_url: 'https://developers.openai.com/api/docs/bots',
        public_keys: [],
    },
    // ADR-0159 addition — grade-A vendor-documented ads bot, the fourth
    // of OpenAI's four-bot roster alongside GPTBot, OAI-SearchBot, and
    // ChatGPT-User. Additive-only per ADR-0060.
    //   https://developers.openai.com/api/docs/bots
    {
        agent_id: 'agent_openai_adsbot',
        category: 'ai_crawler',
        organization: 'OpenAI',
        user_agent_pattern: `\\bOAI-AdsBot/${V}`,
        sec_ch_ua_sill_agent: '"OAI-AdsBot"',
        ip_ranges: [{ url: 'https://openai.com/adsbot.json', format: 'google_json' }],
        public_keys: [],
    },
    // -- Google ------------------------------------------------------------
    {
        agent_id: 'agent_google_gemini',
        category: 'ai_crawler',
        organization: 'Google',
        // ADR-0229 — Google-Extended is a robots.txt CONTROL TOKEN only. Google
        // documents that it has no HTTP user agent of its own (crawling uses
        // the existing Googlebot user agents), so a request whose UA carries
        // `Google-Extended` can only be an impersonator. The seed stays in the
        // floor (canary id, labels, simulation roster) but the matcher skips
        // it (`robots_txt_only`). Before 2026-10-06 this entry matched the
        // token as a UA and therefore only ever matched spoofs.
        //   https://developers.google.com/search/docs/crawling-indexing/google-common-crawlers
        user_agent_pattern: `\\bGoogle-Extended\\b`,
        robots_txt_only: true,
        docs_url: 'https://developers.google.com/search/docs/crawling-indexing/google-common-crawlers',
        public_keys: [],
    },
    // -- Perplexity --------------------------------------------------------
    {
        agent_id: 'agent_perplexity',
        category: 'ai_crawler',
        organization: 'Perplexity',
        user_agent_pattern: `\\bPerplexityBot/${V}`,
        sec_ch_ua_sill_agent: '"PerplexityBot"',
        ip_ranges: [{ url: 'https://www.perplexity.ai/perplexitybot.json', format: 'google_json' }],
        docs_url: 'https://docs.perplexity.ai/guides/bots',
        public_keys: [],
    },
    // ADR-0159 addition — grade-A vendor-documented user-initiated fetch
    // UA. Perplexity's own docs state robots.txt is "generally ignored"
    // for this token (user-initiated, not indexing). Additive-only per
    // ADR-0060.
    //   https://docs.perplexity.ai/guides/perplexity-crawlers
    {
        agent_id: 'agent_perplexity_user',
        category: 'ai_assistant',
        organization: 'Perplexity',
        user_agent_pattern: `\\bPerplexity-User/${V}`,
        sec_ch_ua_sill_agent: '"Perplexity-User"',
        ip_ranges: [{ url: 'https://www.perplexity.ai/perplexity-user.json', format: 'google_json' }],
        public_keys: [],
    },
    // -- Microsoft (added 2026-06-01) -------------------------------------
    // Bingbot is the canonical Bing crawler AND grounds Microsoft Copilot's
    // web answers. UA is Mozilla-wrapped:
    //   Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible;
    //   bingbot/2.0; +http://www.bing.com/bingbot.htm) Chrome/W.X.Y.Z
    //   Safari/537.36
    // https://www.bing.com/webmasters/help/which-crawlers-does-bing-use-8c184ec0
    {
        agent_id: 'agent_microsoft_bingbot',
        category: 'search',
        organization: 'Microsoft',
        user_agent_pattern: `\\bbingbot/${V}`,
        sec_ch_ua_sill_agent: '"bingbot"',
        // ADR-0232 — Bing's published IP list is stale (ADR-0229 §10), so it
        // is verified by forward-confirmed reverse DNS instead.
        rdns_suffixes: ['search.msn.com'],
        // Security review B2: documented first-party rDNS method → authoritative.
        rdns_authoritative: true,
        public_keys: [],
    },
    // -- Meta (added 2026-06-01) ------------------------------------------
    // Meta-ExternalAgent is Meta's training crawler for the Llama family.
    // UA: `meta-externalagent/1.1 (+https://developers.facebook.com/...)`.
    // https://developers.facebook.com/docs/sharing/webmasters/web-crawlers/
    {
        agent_id: 'agent_meta_externalagent',
        category: 'ai_crawler',
        organization: 'Meta',
        user_agent_pattern: `\\bmeta-externalagent/${V}`,
        sec_ch_ua_sill_agent: '"meta-externalagent"',
        public_keys: [],
    },
    // Meta-ExternalFetcher is the user-initiated counterpart (when a user
    // asks Meta AI a question in WhatsApp / Instagram / Facebook). Real-
    // time, not training. Same docs page.
    {
        agent_id: 'agent_meta_externalfetcher',
        category: 'ai_assistant',
        organization: 'Meta',
        user_agent_pattern: `\\bmeta-externalfetcher/${V}`,
        sec_ch_ua_sill_agent: '"meta-externalfetcher"',
        public_keys: [],
    },
    // ADR-0159 follow-up additions (2026-07-04 grade-A confirmation) —
    // ADR-0159 §"Deliberately deferred (evidence-grade-B)" listed these two
    // Meta crawlers as vendor-mentioned but not adversarially verified. The
    // 2026-07-04 pass verified both live against the vendor URL cited
    // below; the migration companion is
    // services/api/migrations/20260704000040_agent_registry_meta_webindexer_externalads.sql.
    // Additive-only per ADR-0060 (new `agent_id`s, no rename, no removal,
    // canary triple unchanged).
    //   https://developers.facebook.com/docs/sharing/webmasters/web-crawlers/
    //
    // Meta-WebIndexer — "navigates the web to improve Meta AI search result
    // quality for users." Respects robots.txt. UA:
    //   meta-webindexer/1.1 (+/documentation/sharing/webmasters/web-crawlers)
    {
        agent_id: 'agent_meta_webindexer',
        category: 'ai_crawler',
        organization: 'Meta',
        user_agent_pattern: `\\bmeta-webindexer/${V}`,
        sec_ch_ua_sill_agent: '"meta-webindexer"',
        public_keys: [],
    },
    // Meta-ExternalAds — "crawls the web for use cases such as improving
    // advertising and other business-related products." Respects robots.txt.
    // UA: `meta-externalads/1.1 (+/documentation/sharing/webmasters/web-crawlers)`
    {
        agent_id: 'agent_meta_externalads',
        category: 'ai_crawler',
        organization: 'Meta',
        user_agent_pattern: `\\bmeta-externalads/${V}`,
        sec_ch_ua_sill_agent: '"meta-externalads"',
        public_keys: [],
    },
    // -- Apple (added 2026-06-01) -----------------------------------------
    // ADR-0229 — Applebot-Extended is Apple's robots.txt opt-out token for
    // Apple Intelligence training. Apple documents that it does not crawl
    // webpages: crawling is done by Applebot. It is therefore never a real
    // User-Agent, and before 2026-10-06 this entry could only match spoofs.
    // Kept in the floor (labels, simulation roster); the matcher skips it.
    //   https://support.apple.com/en-us/119829
    {
        agent_id: 'agent_apple_extended',
        category: 'ai_crawler',
        organization: 'Apple',
        user_agent_pattern: `\\bApplebot-Extended\\b`,
        robots_txt_only: true,
        docs_url: 'https://support.apple.com/en-us/119829',
        public_keys: [],
    },
    // -- DuckDuckGo (added 2026-06-01) ------------------------------------
    // DuckAssistBot is DuckDuckGo's retrieval crawler — only fires on user
    // queries that activate DuckAssist (NOT a training crawler).
    // UA: `DuckAssistBot/1.2; (+http://duckduckgo.com/duckassistbot.html)`
    // https://duckduckgo.com/duckassistbot.html
    {
        agent_id: 'agent_duckduckgo_assistbot',
        category: 'ai_assistant',
        organization: 'DuckDuckGo',
        user_agent_pattern: `\\bDuckAssistBot/${V}`,
        sec_ch_ua_sill_agent: '"DuckAssistBot"',
        ip_ranges: [{ url: 'https://duckduckgo.com/duckassistbot.json', format: 'google_json' }],
        docs_url: 'https://duckduckgo.com/duckduckgo-help-pages/results/duckassistbot',
        public_keys: [],
    },
    // -- Mistral (added 2026-06-01) ---------------------------------------
    // MistralAI-User is Mistral's user-initiated fetch UA (Le Chat / Vibe
    // users asking Mistral a question that needs a web visit). NOT a
    // training crawler. UA is Mozilla-wrapped:
    //   Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible;
    //   MistralAI-User/1.0; +https://docs.mistral.ai/robots)
    // https://docs.mistral.ai/robots
    {
        agent_id: 'agent_mistral_user',
        category: 'ai_assistant',
        organization: 'Mistral',
        user_agent_pattern: `\\bMistralAI-User/${V}`,
        sec_ch_ua_sill_agent: '"MistralAI-User"',
        public_keys: [],
    },
    // -- Search & training crawlers (added 2026-08-26) --------------------
    // Traditional search and training-data crawlers that appear in real
    // merchant traffic alongside AI agents. Named here so the dashboard
    // shows a brand identity instead of "unknown agent."
    //
    // Googlebot — Google's primary search crawler. Separate from
    // Google-Extended (Gemini training opt-out) above.
    // UA: Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)
    // https://developers.google.com/search/docs/crawling-indexing/google-ext-crawlers
    {
        agent_id: 'agent_google_searchbot',
        category: 'search',
        organization: 'Google',
        user_agent_pattern: `\\bGooglebot/${V}`,
        sec_ch_ua_sill_agent: '"Googlebot"',
        ip_ranges: [{ url: 'https://developers.google.com/static/crawling/ipranges/common-crawlers.json', format: 'google_json' }],
        rdns_suffixes: GOOGLE_COMMON_CRAWLER_RDNS,
        // Security review B1/B2 (2026-10-11): Google documents both the
        // common-crawlers list and googlebot.com reverse DNS as the way to
        // verify Googlebot — off-list / out-of-suffix may read as Mismatch.
        ip_list_complete: true,
        rdns_authoritative: true,
        docs_url: 'https://developers.google.com/crawling/docs/crawlers-fetchers/google-common-crawlers',
        public_keys: [],
    },
    // CommonCrawl CCBot — open web crawl archive used as training data by
    // many LLMs (GPT, LLaMA, Mistral, etc.). Opt-out via robots.txt.
    // UA: CCBot/2.0 (https://commoncrawl.org/faq/)
    // https://commoncrawl.org/faq/
    {
        agent_id: 'agent_commoncrawl',
        category: 'ai_crawler',
        organization: 'Common Crawl',
        user_agent_pattern: `\\bCCBot/${V}`,
        sec_ch_ua_sill_agent: '"CCBot"',
        ip_ranges: [{ url: 'https://index.commoncrawl.org/ccbot.json', format: 'google_json' }],
        web_bot_auth_signers: ['commoncrawl.org'],
        docs_url: 'https://commoncrawl.org/ccbot',
        public_keys: [],
    },
    // -- AI research agents (added 2026-08-26) ----------------------------
    // Reflectionbot — reflection.ai's web retrieval agent. Seen across
    // multiple merchant sites in live traffic analysis (2026-08-26).
    // UA: Mozilla/5.0 (compatible; Reflectionbot/1.0; +https://reflection.ai/bot)
    // https://reflection.ai/bot
    {
        agent_id: 'agent_reflection_bot',
        category: 'ai_crawler',
        organization: 'Reflection AI',
        user_agent_pattern: `\\bReflectionbot/${V}`,
        sec_ch_ua_sill_agent: '"Reflectionbot"',
        public_keys: [],
    },
    // -- ADR-0229 additions (2026-10-06) ---------------------------------
    // Each token below is taken verbatim from the operator's OWN
    // documentation (docs_url). Spoof-cluster names without first-party UA
    // documentation (DeepSeek, Qwen, Yi, Hunyuan, Pangu, ChatGLM, xAI/Grok,
    // Cohere, Brave) are deliberately NOT registered — see ADR-0229 §2.
    //
    // Google image / video crawlers — `Googlebot-Image/1.0`,
    // `Googlebot-Video/1.0`. (Googlebot-News is a robots.txt token with no
    // UA of its own, so it is not registered.)
    {
        agent_id: 'agent_google_image_bot',
        category: 'search',
        organization: 'Google',
        user_agent_pattern: `\\bGooglebot-Image/${V}`,
        ip_ranges: [{ url: 'https://developers.google.com/static/crawling/ipranges/common-crawlers.json', format: 'google_json' }],
        rdns_suffixes: GOOGLE_COMMON_CRAWLER_RDNS,
        // Security review B1/B2 (2026-10-11): Google documents both the
        // common-crawlers list and googlebot.com reverse DNS as the way to
        // verify Googlebot — off-list / out-of-suffix may read as Mismatch.
        ip_list_complete: true,
        rdns_authoritative: true,
        docs_url: 'https://developers.google.com/crawling/docs/crawlers-fetchers/google-common-crawlers',
        public_keys: [],
    },
    {
        agent_id: 'agent_google_video_bot',
        category: 'search',
        organization: 'Google',
        user_agent_pattern: `\\bGooglebot-Video/${V}`,
        ip_ranges: [{ url: 'https://developers.google.com/static/crawling/ipranges/common-crawlers.json', format: 'google_json' }],
        rdns_suffixes: GOOGLE_COMMON_CRAWLER_RDNS,
        // Security review B1/B2 (2026-10-11): Google documents both the
        // common-crawlers list and googlebot.com reverse DNS as the way to
        // verify Googlebot — off-list / out-of-suffix may read as Mismatch.
        ip_list_complete: true,
        rdns_authoritative: true,
        docs_url: 'https://developers.google.com/crawling/docs/crawlers-fetchers/google-common-crawlers',
        public_keys: [],
    },
    // ADR-0232 — Google-InspectionTool (Search Console URL inspection, Rich
    // Results Test), a documented common crawler:
    //   `Mozilla/5.0 (compatible; Google-InspectionTool/1.0)`
    {
        agent_id: 'agent_google_inspection_tool',
        category: 'search',
        organization: 'Google',
        user_agent_pattern: `\\bGoogle-InspectionTool/${V}`,
        ip_ranges: [{ url: 'https://developers.google.com/static/crawling/ipranges/common-crawlers.json', format: 'google_json' }],
        rdns_suffixes: GOOGLE_COMMON_CRAWLER_RDNS,
        // Security review B1/B2 (2026-10-11): Google documents both the
        // common-crawlers list and googlebot.com reverse DNS as the way to
        // verify Googlebot — off-list / out-of-suffix may read as Mismatch.
        ip_list_complete: true,
        rdns_authoritative: true,
        docs_url: 'https://developers.google.com/crawling/docs/crawlers-fetchers/google-common-crawlers',
        public_keys: [],
    },
    // Applebot — Apple's real crawler (Siri / Spotlight search; its data
    // may train Apple models unless Applebot-Extended is disallowed).
    //   `Mozilla/5.0 ... (Applebot/0.1; +http://www.apple.com/go/applebot)`
    {
        agent_id: 'agent_apple_applebot',
        category: 'search',
        organization: 'Apple',
        user_agent_pattern: `\\bApplebot/${V}`,
        ip_ranges: [{ url: 'https://search.developer.apple.com/applebot.json', format: 'google_json' }],
        rdns_suffixes: ['applebot.apple.com'],
        // Security review B2: documented first-party rDNS method → authoritative.
        rdns_authoritative: true,
        docs_url: 'https://support.apple.com/en-us/119829',
        public_keys: [],
    },
    // -- 2026-10-11 seed audit (labels-review DECISIONS.md §Data fixes #4).
    //    Each entry cites the vendor page its verification method came
    //    from. `rdns_suffixes` now drive a merchant-visible verdict (live
    //    rDNS, Confirmed / Mismatch), so a suffix is only listed when the
    //    vendor's own documentation names it — a wrong suffix would accuse
    //    the real crawler.
    // Huawei PetalBot — Petal Search's crawler. Huawei documents verifying
    // it by forward-confirmed reverse DNS to `*.petalsearch.com` (observed
    // PTRs: `petalbot-<ip>.petalsearch.com`) or `*.aspiegel.com` (Aspiegel
    // is Huawei's Irish search subsidiary). No published IP list, no Web
    // Bot Auth signer. Traffic originates from Huawei Cloud, ASN AS136907.
    //   https://webmaster.petalsearch.com/site/petalbot
    //   (aspiegel.com/petalbot, cited in older UAs, now returns 404 —
    //   suffixes per the founder-approved brief 2026-10-11; re-check when
    //   Huawei's page is reachable without JS.)
    {
        agent_id: 'agent_petalbot',
        category: 'search',
        organization: 'Huawei (Petal Search)',
        // `Mozilla/5.0 (compatible; PetalBot;+https://webmaster.petalsearch.com/site/petalbot)`
        // carries no version, so the pattern is the bare token.
        user_agent_pattern: `\\bPetalBot\\b`,
        sec_ch_ua_sill_agent: '"PetalBot"',
        // Confirm-only (security review B2): suffixes not confirmed from a
        // first-party page, so a mismatch never becomes a Mismatch.
        // TODO(security re-review follow-up 3): re-confirm petalsearch.com /
        // aspiegel.com against Huawei's first-party PetalBot page before ever
        // setting rdns_authoritative here.
        rdns_suffixes: ['petalsearch.com', 'aspiegel.com'],
        docs_url: 'https://webmaster.petalsearch.com/site/petalbot',
        public_keys: [],
    },
    // Yandex — "All Yandex robots have names ending in yandex.ru,
    // yandex.net or yandex.com." The IP list "is not disclosed"
    // (AS13238, AS208722, AS212066).
    //   https://yandex.com/support/webmaster/robot-workings/check-yandex-robots.html
    //   (fetched 2026-10-11)
    {
        agent_id: 'agent_yandexbot',
        category: 'search',
        organization: 'Yandex',
        user_agent_pattern: `\\bYandex(?:Bot|Images|Video|Mobile(?:Bot)?|Accessibility(?:Bot)?|RenderResourcesBot)/${V}`,
        sec_ch_ua_sill_agent: '"YandexBot"',
        rdns_suffixes: ['yandex.ru', 'yandex.net', 'yandex.com'],
        // Security review B2: documented first-party rDNS method → authoritative.
        rdns_authoritative: true,
        docs_url: 'https://yandex.com/support/webmaster/robot-workings/check-yandex-robots.html',
        public_keys: [],
    },
    // Baidu — Baidu's spider help says to run `host <ip>` and check the
    // name ends in `*.baidu.com` or `*.baidu.jp` (observed PTRs:
    // `baiduspider-<ip>.crawl.baidu.com`). No published IP list.
    //   https://www.baidu.com/search/spider.html
    //   https://help.baidu.com/question?prod_id=99&class=476&id=3001
    //   (the official pages did not render for a non-browser fetch on
    //   2026-10-11; the suffixes are the long-standing documented pair.
    //   SECURITY REVIEW: confirm against the live page before enabling.)
    {
        agent_id: 'agent_baiduspider',
        category: 'search',
        organization: 'Baidu',
        user_agent_pattern: `\\bBaiduspider(?:-image|-video|-news|-render|-favo|-ads)?/${V}`,
        sec_ch_ua_sill_agent: '"Baiduspider"',
        // Confirm-only (security review B2), as for PetalBot.
        // TODO(security re-review follow-up 3): re-confirm baidu.com / baidu.jp
        // against Baidu's first-party spider page before ever setting
        // rdns_authoritative here.
        rdns_suffixes: ['baidu.com', 'baidu.jp'],
        docs_url: 'https://www.baidu.com/search/spider.html',
        public_keys: [],
    },
    // Sogou — name-only seed. Sogou's webmaster help page could not be
    // fetched on 2026-10-11 and no first-party statement of a reverse-DNS
    // suffix or IP list was found, so NO verification method is declared
    // (Unconfirmed, never Mismatch).
    //   https://www.sogou.com/docs/help/webmasters.htm
    {
        agent_id: 'agent_sogou',
        category: 'search',
        organization: 'Sogou',
        user_agent_pattern: `\\bSogou (?:web|inst|News|Orion|Pic) spider/${V}`,
        sec_ch_ua_sill_agent: '"Sogou web spider"',
        docs_url: 'https://www.sogou.com/docs/help/webmasters.htm',
        public_keys: [],
    },
    // DuckDuckBot — DuckDuckGo's search crawler (NOT DuckAssistBot, which
    // is registered separately). The help page lists the IPs and says they
    // are "also available in JSON at https://duckduckgo.com/duckduckbot.json"
    // (Google-style `prefixes[].ipv4Prefix`, verified 2026-10-11).
    //   https://duckduckgo.com/duckduckgo-help-pages/results/duckduckbot
    {
        agent_id: 'agent_duckduckbot',
        category: 'search',
        organization: 'DuckDuckGo',
        user_agent_pattern: `\\bDuckDuckBot/${V}`,
        sec_ch_ua_sill_agent: '"DuckDuckBot"',
        ip_ranges: [{ url: 'https://duckduckgo.com/duckduckbot.json', format: 'google_json' }],
        docs_url: 'https://duckduckgo.com/duckduckgo-help-pages/results/duckduckbot',
        public_keys: [],
    },
    // Bingbot — already wired for rDNS (`search.msn.com`). Microsoft also
    // serves https://www.bing.com/toolbox/bingbot.json, but ADR-0232 moved
    // Bingbot to rDNS because that list went stale; unchanged here.
    // Exa — signs every request with Web Bot Auth (ADR-0214 §5: verified
    // byte-for-byte on vbout.com 2026-09-25). No IP list published.
    //   `Mozilla/5.0 (compatible; ExaSearchBot/1.0; +https://crawler.exa.ai/)`
    {
        agent_id: 'agent_exa_searchbot',
        category: 'ai_crawler',
        organization: 'Exa',
        user_agent_pattern: `\\bExaSearchBot/${V}`,
        web_bot_auth_signers: ['crawler.exa.ai'],
        docs_url: 'https://crawler.exa.ai/',
        public_keys: [],
    },
    // Amazon — three documented tokens with the same wrapper:
    //   `Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible;
    //    Amazonbot/0.1) Chrome/W.X.Y.Z Safari/537.36`
    // Amazon's IP lists are HTML pages with inconsistent JSON keys, so they
    // are not wired as ip_ranges (ADR-0229 §3).
    {
        agent_id: 'agent_amazonbot',
        category: 'ai_crawler',
        organization: 'Amazon',
        user_agent_pattern: `\\bAmazonbot/${V}`,
        docs_url: 'https://developer.amazon.com/amazonbot',
        public_keys: [],
    },
    {
        agent_id: 'agent_amazon_searchbot',
        category: 'ai_crawler',
        organization: 'Amazon',
        user_agent_pattern: `\\bAmzn-SearchBot/${V}`,
        docs_url: 'https://developer.amazon.com/amazonbot',
        public_keys: [],
    },
    {
        agent_id: 'agent_amazon_user',
        category: 'ai_assistant',
        organization: 'Amazon',
        user_agent_pattern: `\\bAmzn-User/${V}`,
        docs_url: 'https://developer.amazon.com/amazonbot',
        public_keys: [],
    },
    // ByteDance Bytespider — the UA carries no version:
    //   `Mozilla/5.0 (compatible; Bytespider; https://zhanzhang.toutiao.com/) ...`
    {
        agent_id: 'agent_bytespider',
        category: 'ai_crawler',
        organization: 'ByteDance',
        user_agent_pattern: `\\bBytespider\\b`,
        docs_url: 'https://zhanzhang.toutiao.com/docs/intro/26899',
        public_keys: [],
    },
    // Linkup — `LinkupBot/1.0 (LinkupBot for web indexing; https://www.linkup.so/bot; bot@linkup.so)`
    {
        agent_id: 'agent_linkup_bot',
        category: 'ai_crawler',
        organization: 'Linkup',
        user_agent_pattern: `\\bLinkupBot/${V}`,
        ip_ranges: [{ url: 'https://www.linkup.so/linkupbot-ips.txt', format: 'plain_list' }],
        docs_url: 'https://linkup.so/bot',
        public_keys: [],
    },
    // Parallel Web Systems — `...; compatible; ShapBot/0.1.0` (crawler) and
    // `...; compatible; Shap-User/0.1.0` (user-initiated fetch).
    {
        agent_id: 'agent_parallel_shapbot',
        category: 'ai_crawler',
        organization: 'Parallel Web Systems',
        user_agent_pattern: `\\bShapBot/${V}`,
        ip_ranges: [{ url: 'https://docs.parallel.ai/resources/shapbot.json', format: 'google_json' }],
        docs_url: 'https://parallel.ai/parallel-web-systems-bots',
        public_keys: [],
    },
    {
        agent_id: 'agent_parallel_shap_user',
        category: 'ai_assistant',
        organization: 'Parallel Web Systems',
        user_agent_pattern: `\\bShap-User/${V}`,
        docs_url: 'https://parallel.ai/parallel-web-systems-bots',
        public_keys: [],
    },
    // Moonshot AI / Kimi — three documented tokens, each with a published
    // IP list (`...; compatible; KimiBot/1.0; +https://www.kimi.com/policies/kimi-crawlers`).
    // The kimi.com list URLs 302 to kimi.ai; the fetcher follows no
    // redirects, so the kimi.ai URLs are wired directly.
    {
        agent_id: 'agent_moonshot_kimibot',
        category: 'ai_crawler',
        organization: 'Moonshot AI',
        user_agent_pattern: `\\bKimiBot/${V}`,
        ip_ranges: [{ url: 'https://www.kimi.ai/policies/kimibot.json', format: 'google_json' }],
        docs_url: 'https://www.kimi.com/policies/kimi-crawlers',
        public_keys: [],
    },
    {
        agent_id: 'agent_moonshot_kimi_searchbot',
        category: 'ai_crawler',
        organization: 'Moonshot AI',
        user_agent_pattern: `\\bKimi-SearchBot/${V}`,
        ip_ranges: [{ url: 'https://www.kimi.ai/policies/kimi-searchbot.json', format: 'google_json' }],
        docs_url: 'https://www.kimi.com/policies/kimi-crawlers',
        public_keys: [],
    },
    {
        agent_id: 'agent_moonshot_kimi_user',
        category: 'ai_assistant',
        organization: 'Moonshot AI',
        user_agent_pattern: `\\bKimi-User/${V}`,
        ip_ranges: [{ url: 'https://www.kimi.ai/policies/kimi-user.json', format: 'google_json' }],
        docs_url: 'https://www.kimi.com/policies/kimi-crawlers',
        public_keys: [],
    },
    // You.com — `...; compatible; YouBot/1.0; +https://docs.you.com/youbot; env:prod) ...`
    // Signs with Web Bot Auth (key directory at you.com). Its single IP
    // range is published inline only (no machine-readable list).
    {
        agent_id: 'agent_you_youbot',
        category: 'ai_crawler',
        organization: 'You.com',
        user_agent_pattern: `\\bYouBot/${V}`,
        web_bot_auth_signers: ['you.com'],
        docs_url: 'https://you.com/docs/youbot',
        public_keys: [],
    },
    // -- Social link-preview bots (added 2026-06-01) ----------------------
    // These are NOT AI agents — they're the URL-preview crawlers that
    // social platforms run when a user pastes a link. They commonly
    // appear in audit logs and are worth identifying so the merchant
    // doesn't have to triage them as "unknown agent." Each platform
    // sends its own UA; the user-supplied composite UA
    // (`GoogleMessages/X facebookexternalhit/Y Facebot Twitterbot/Z`)
    // matches the first registry entry whose regex hits, by precedence.
    {
        agent_id: 'agent_x_twitterbot',
        category: 'social_preview',
        organization: 'X (Twitter)',
        // Twitterbot is the URL-preview crawler for X/Twitter. Documented
        // at https://developer.x.com/en/docs/tweets/optimize-with-cards/guides/troubleshooting-cards
        user_agent_pattern: `\\bTwitterbot/${V}`,
        sec_ch_ua_sill_agent: '"Twitterbot"',
        public_keys: [],
    },
    {
        agent_id: 'agent_meta_facebook_external',
        category: 'social_preview',
        organization: 'Meta',
        // facebookexternalhit is the Facebook URL-preview crawler that
        // fetches Open Graph metadata when a link is shared in FB/Messenger.
        // https://developers.facebook.com/docs/sharing/webmasters/web-crawlers/
        user_agent_pattern: `\\bfacebookexternalhit/${V}`,
        sec_ch_ua_sill_agent: '"facebookexternalhit"',
        public_keys: [],
    },
    {
        agent_id: 'agent_meta_facebot',
        category: 'social_preview',
        organization: 'Meta',
        // Facebot — Facebook's secondary preview crawler (no version segment
        // in the UA, so we use a word-boundary-only pattern).
        user_agent_pattern: `\\bFacebot\\b`,
        sec_ch_ua_sill_agent: '"Facebot"',
        public_keys: [],
    },
    {
        agent_id: 'agent_linkedin_bot',
        category: 'social_preview',
        organization: 'LinkedIn',
        // LinkedInBot — URL-preview crawler for LinkedIn link sharing.
        user_agent_pattern: `\\bLinkedInBot/${V}`,
        sec_ch_ua_sill_agent: '"LinkedInBot"',
        public_keys: [],
    },
    {
        agent_id: 'agent_slack_link_expander',
        category: 'social_preview',
        organization: 'Slack',
        // Slackbot-LinkExpanding — fires when a Slack user pastes a link
        // and Slack fetches the page to render the unfurl. ADR-0229 widened
        // the pattern: Slack's documented UAs separate name and version with
        // a SPACE (`Slackbot-LinkExpanding 1.0 (+https://api.slack.com/robots)`)
        // so the old `/`-only pattern missed them, and the plain
        // `Slackbot 1.0 (+https://api.slack.com/robots)` fetcher is the same
        // family. `[ /]` is a single-character class — no ReDoS surface.
        //   https://api.slack.com/robots
        user_agent_pattern: `\\bSlackbot(?:-LinkExpanding)?[ /]${V}`,
        docs_url: 'https://api.slack.com/robots',
        sec_ch_ua_sill_agent: '"Slackbot-LinkExpanding"',
        public_keys: [],
    },
    {
        agent_id: 'agent_discord_bot',
        category: 'social_preview',
        organization: 'Discord',
        // Discordbot — URL-preview crawler for Discord embeds.
        user_agent_pattern: `\\bDiscordbot/${V}`,
        sec_ch_ua_sill_agent: '"Discordbot"',
        public_keys: [],
    },
    // -- Sill internal test-fixture (ADR-0061 §3.1) -----------------------
    // The seeded test-mode mandate signer (`agent_sill_test_fixture`). The
    // PUBLIC key lives here; the SECRET key is held ONLY in the `sill-api`
    // Fly secret `SILL_TEST_FIXTURE_AGENT_SECRET_KEY` and in the operator's
    // password manager (never in source). The runbook helper at
    // `services/api/scripts/test-fixture-sign-mandate.ts` reads the secret
    // from env, builds + signs a `SignedMandateBody`, prints `{ signed,
    // envelope }` to stdout, and exits — the secret never enters argv or
    // shell history.
    //
    // Blast radius is bounded by the dogfood site's active policy: r01 caps
    // spending by `agent_id === 'agent_sill_test_fixture'` at $19.99 USD
    // per mandate; test-mode Stripe egress only (no real money). Rotation:
    // mint new keypair → update the `public_key` below → edge redeploy →
    // set new `SILL_TEST_FIXTURE_AGENT_SECRET_KEY` Fly secret → flip the
    // previous key's bundled status to `revoked` (the file-top ADR-0006/
    // ADR-0060 "additive-only" semantics apply: KV cannot override a
    // bundled identity's keys, so a rotation MUST ship via this file).
    //
    // `user_agent_pattern` matches a deliberately distinctive token so an
    // operator running the helper locally sees their visits flagged as
    // test traffic on the dashboard.
    //
    // `visibility: 'internal'` quarantines this agent from the dashboard
    // agent picker (ADR-0061 §3.1). The verifier path does NOT branch on
    // visibility — identification + signature checks behave identically
    // to a public agent.
    //
    // PLACEHOLDER PUBLIC KEY: this base64url string is shape-valid (43
    // chars unpadded base64url for a 32-byte ed25519 key) but is NOT a
    // real keypair public-half. Ops MUST run the implementer-supplied
    // mint procedure and replace this value before the smoke runbook can
    // verify a signed mandate.
    {
        agent_id: 'agent_sill_test_fixture',
        category: 'internal',
        organization: 'Sill (internal test fixture)',
        user_agent_pattern: `\\bSillTestFixture/${V}`,
        sec_ch_ua_sill_agent: '"SillTestFixture"',
        visibility: 'internal',
        public_keys: [
            {
                key_id: 'key_sill_test_v1',
                // ADR-0061 §3.2 — real test-mode ed25519 public key. The
                // matching 32-byte seed lives ONLY in Fly secret
                // `SILL_TEST_FIXTURE_AGENT_SECRET_KEY` (sign via
                // `services/api/scripts/test-fixture-sign-mandate.ts`). Rotation
                // procedure: `docs/runbooks/stripe-test-mode-e2e-smoke.md`.
                public_key: 'QDRSX3Bs2osGjrRxLxms8G7swOtW_BDud-sQklRstIo',
                algorithm: 'ed25519',
                active: true,
            },
        ],
    },
    // -- Sill Heartbeat (ADR-0176 §4) -------------------------------------
    // The account-scoped agent smoke-test harness (ADR-0176) mints signed
    // mandates as this identity and POSTs them through the SAME public edge
    // pipeline a real agent hits (POST /v1/mcp/{site_key}). Unlike
    // `agent_sill_test_fixture` (visibility 'internal', capped to the
    // dogfood site's r05 rule), Heartbeat is a FIRST-PARTY, VISIBLE agent
    // (`visibility: 'public'`) — merchants see "Sill Heartbeat" in their
    // live-agent view and audit log. The verifier path does NOT branch on
    // visibility; identification + signature checks behave identically to
    // any other bundled agent.
    //
    // Additive-only per ADR-0060: NEW `agent_id`, no rename, no removal,
    // canary triple (Anthropic Claude / OpenAI GPT / Google Gemini)
    // unchanged. The KV writer's TEST_AGENT_ID_RE
    // (`^agent_(?:[a-z0-9]+_)*(?:phase2_test|smoke|verify)(?:_|$)`) does
    // NOT match `agent_sill_heartbeat` — `heartbeat` is not one of the
    // three forbidden segment tokens (phase2_test / smoke / verify).
    //
    // The PUBLIC key below is a PLACEHOLDER (shape-valid base64url — 43
    // chars unpadded — but NOT a real keypair public-half). Before the
    // harness can verify a signed mandate in production, an operator MUST
    // (1) run the mint procedure from
    // `docs/runbooks/stripe-test-mode-e2e-smoke.md` to generate a new
    // ed25519 keypair, (2) replace this value with the emitted public
    // half, and (3) set the matching 32-byte seed as the Fly secret
    // `SILL_HEARTBEAT_AGENT_SECRET_KEY` on the `sill-api` app. Rotation
    // procedure is identical to `agent_sill_test_fixture`'s.
    //
    // Blast radius while dormant (placeholder key): the mandate verifier
    // rejects every signature because it cannot match the placeholder;
    // the runner would emit `verification_rejected` for every dispatch.
    // Blast radius while live: bounded by (a) the runner's hardcoded
    // `intent.max_amount = 5.00 USD` ceiling and (b) any r05 rule on the
    // merchant's active policy — Heartbeat is subject to r05 exactly like
    // any other agent (no bypass). The runner refuses to launch a Tier-1
    // run if any active policy caps agent_sill_heartbeat below $5.00.
    {
        agent_id: 'agent_sill_heartbeat',
        category: 'internal',
        organization: 'Sill',
        user_agent_pattern: `\\bSillHeartbeat/${V}`,
        sec_ch_ua_sill_agent: '"SillHeartbeat"',
        visibility: 'public',
        public_keys: [
            {
                key_id: 'key_sill_heartbeat_v1',
                // Real ed25519 public key, minted + activated 2026-07-10
                // (security-reviewed). Secret seed lives ONLY as the `sill-api`
                // Fly secret `SILL_HEARTBEAT_AGENT_SECRET_KEY` + the operator's
                // password manager — never in source, per ADR-0061 §3.1's
                // key-management pattern (mirrors `agent_sill_test_fixture`
                // above). Rotation: mint a new keypair → PR that swaps this
                // value → edge redeploy → set the new Fly secret → mark this
                // `key_id` `active: false` on the next redeploy.
                public_key: 'Qc2moL9Opm4799BURLxHrhDzWrQyUXT9TBrr_rdVUBI',
                algorithm: 'ed25519',
                active: true,
            },
        ],
    },
];
export const SILL_INTERNAL_TOOLS = [
    // 2026-10-08 — ADR-0238 Slice 0: the public-scan/site-scan probes now all
    // send one token (`Sill-Readiness/1 (+https://sill.so/bot)`) with the
    // single bot page at /bot. `agent_sill_analyzer` is kept here as a
    // recognised LEGACY sample for ~90 days so a merchant's own log analytics
    // and the agent-registry recognise historical requests; no code path
    // sends `Sill-Analyzer/` anymore from the readiness/site-scan surface
    // (profile-extraction's scraper + verify-card-signature still carry
    // the old token until their own ADR-0238 follow-ups land).
    { agent_id: 'agent_sill_analyzer', token: 'Sill-Analyzer', name: 'Site scanner', purpose: 'Legacy UA for the site scanner before ADR-0238. Still recognised; readiness/site-scan now sends Sill-Readiness/1.', merchant_purpose: 'Sill checking your site for AI-agent readiness.', sample: 'Sill-Analyzer/1 (+https://sill.so/analyzer)' },
    { agent_id: 'agent_sill_readiness', token: 'Sill-Readiness', name: 'Site scanner', purpose: 'Sill scanning a site for agent-readiness: homepage, robots.txt, sitemap, llms.txt, agent card, MCP, UCP, ARD, api-catalog, agent gateway.', merchant_purpose: 'Sill checking your site for AI-agent readiness.', sample: 'Sill-Readiness/1 (+https://sill.so/bot)', signed: true },
    { agent_id: 'agent_sill_verifier', token: 'Sill-Verify', name: 'Verifier', purpose: 'Sill fetching an operator key directory or published IP list to verify a visiting agent.', merchant_purpose: 'Sill checking a visiting agent\'s published keys or addresses.', sample: 'Sill-Verify/1 (+https://sill.so/detect)' },
    { agent_id: 'agent_sill_ucp', token: 'Sill-UCP', name: 'UCP client', purpose: 'Sill fetching a UCP platform profile or delivering a UCP order webhook.', merchant_purpose: 'Sill exchanging order details with a shopping platform for your store.', sample: 'Sill-UCP/1 (+https://sill.so)' },
    { agent_id: 'agent_sill_pap', token: 'Sill-PAP', name: 'PAP client', purpose: 'Sill fetching a Personal Agent Protocol client_id metadata document or its JWKS during a Session Token request (ADR-0252).', merchant_purpose: 'Sill verifying the key of the AI agent that opened a session with you.', sample: 'Sill-PAP/1 (+https://docs.sill.so/protocols/pap)' },
    { agent_id: 'agent_sill_webhook', token: 'Sill-Webhook', name: 'Webhook', purpose: 'Sill delivering a signed custom-skill webhook to the merchant endpoint.', merchant_purpose: 'Sill sending an event to your custom skill\'s webhook.', sample: 'Sill-Webhook/1' },
    { agent_id: 'agent_sill_webhook_probe', token: 'Sill-Webhook-Probe', name: 'Webhook probe', purpose: 'Sill checking that a merchant webhook endpoint answers.', merchant_purpose: 'Sill checking that your custom skill\'s webhook answers.', sample: 'Sill-Webhook-Probe/1' },
    { agent_id: 'agent_sill_mcp', token: 'Sill-MCP', name: 'MCP dispatch', purpose: 'Sill calling a merchant MCP endpoint on behalf of a custom skill.', merchant_purpose: 'Sill calling your MCP endpoint for a custom skill.', sample: 'Sill-MCP/1' },
    { agent_id: 'agent_sill_assistant_mcp', token: 'sill-assistant-mcp', name: 'Assistant MCP dispatch', purpose: 'The Sill account MCP server calling Sill\'s own dashboard API in-process on behalf of a merchant\'s connected assistant (ADR-0261). Never leaves the API.', merchant_purpose: 'Sill acting inside your account for an assistant you connected, such as Claude or ChatGPT.', sample: 'sill-assistant-mcp/1 (claude.ai)' },
    { agent_id: 'agent_sill_reference_agent', token: 'Sill-Reference-Agent', name: 'Reference test agent', purpose: 'The ops reference agent exercising PAP and UCP protocol surfaces against a Sill site (ops2026 protocol test).', merchant_purpose: 'Sill testing its own protocol endpoints against your site.', sample: 'Sill-Reference-Agent/1 (+https://sill.so/reference-agent)' },
    { agent_id: 'agent_sill_detect', token: 'Sill-Detect', name: 'Platform detection', purpose: 'Sill detecting which platform a site is built on (WordPress, Shopify, WooCommerce and others).', merchant_purpose: 'Sill checking which platform your site is built on, so setup shows the right install for it.', sample: 'Sill-Detect/1 (+https://sill.so/detect)', signed: true },
    { agent_id: 'agent_sill_coverage', token: 'Sill-Coverage', name: 'Install coverage check', purpose: 'Sill checking which install paths are live on a site.', merchant_purpose: 'Sill checking which Sill install paths are live on your site.', sample: 'Sill-Coverage/1 (+https://sill.so/detect)', signed: true },
    { agent_id: 'agent_sill_conformance', token: 'Sill-Conformance', name: 'Protocol check', purpose: 'Sill running its protocol conformance check (agent card, MCP, A2A, UCP, PAP) against a site it serves.', merchant_purpose: 'Sill checking that your agent endpoints answer correctly.', sample: 'Sill-Conformance/1 (+https://sill.so/bot)' },
    { agent_id: 'agent_sill_compatibility', token: 'Sill-Compatibility', name: 'Compatibility probe', purpose: 'Sill listing the tools of an MCP server for the compatibility report.', merchant_purpose: 'Sill listing the tools your MCP server offers.', sample: 'Sill-Compatibility/1 (+https://sill.so/compatibility)', signed: true },
    { agent_id: 'agent_sill_activation_probe', token: 'Sill-Activation-Probe', name: 'Skill activation probe', purpose: 'Sill testing a custom skill endpoint before it is switched on.', merchant_purpose: 'Sill testing a custom skill before it is switched on.', sample: 'Sill-Activation-Probe/1' },
    { agent_id: 'agent_sill_cf_worker', token: 'Sill-CF-Worker', name: 'Cloudflare Worker', purpose: 'The Sill Cloudflare Worker on a merchant site fetching its configuration.', merchant_purpose: 'The Sill Cloudflare Worker on your site fetching its settings.', sample: 'Sill-CF-Worker/1.5' },
    { agent_id: 'agent_sill_wp_plugin', token: 'Sill-WP-Plugin', name: 'WordPress plugin', purpose: 'The Sill WordPress plugin on a merchant site fetching its configuration.', merchant_purpose: 'The Sill WordPress plugin on your site fetching its settings.', sample: 'Sill-WP-Plugin/1.5' },
    { agent_id: 'agent_sill_install_verify', token: 'Sill-Install-Verify', name: 'Install verify', purpose: 'Sill fetching a merchant well-known file after a redeploy to confirm the new install version is live.', merchant_purpose: 'Sill confirming your latest Sill install is live.', sample: 'Sill-Install-Verify/1 (+https://sill.so/bot)', signed: true },
    { agent_id: 'agent_sill_proof', token: 'Sill-Proof', name: 'Ownership check', purpose: 'Sill checking the site for its install proof: the snippet on the homepage or /.well-known/sill-proof.txt.', merchant_purpose: 'Sill confirming that you own this site.', sample: 'Sill-Proof/1 (+https://sill.so/bot)', signed: true },
    // ADR-0259 — one-shot signed visit right after a site is verified, so
    // the dashboard's Activity view is not empty on the first look. The
    // token is dashless on purpose: Sill tools with a non-`sill-` prefix are
    // auto-added to `SILL_OWN_UA_SQL_RE` (rollup / classifier exclusion),
    // Activity never reads that regex, so this hit stays visible in
    // Activity but never inflates benchmarks or auto-classifier output.
    { agent_id: 'agent_sill_welcome', token: 'SillWelcome', name: 'Welcome visit', purpose: 'Sill sending one verified visit to a newly-proved merchant site so the Activity view has its first real agent hit end-to-end.', merchant_purpose: 'Sill welcoming your newly verified site with its first visit. AI agents that visit from now on appear here, like this one.', sample: 'SillWelcome/1 (+https://sill.so/bot)', signed: true },
    { agent_id: 'agent_sill_connector_shopify', token: 'Sill-Connector-Shopify', name: 'Shopify connector', purpose: 'Sill calling the Shopify Admin API for a connected store.', merchant_purpose: 'Sill reading your Shopify store through the connection you set up.', sample: 'Sill-Connector-Shopify/1' },
    { agent_id: 'agent_sill_connector_shopify_storefront', token: 'Sill-Connector-Shopify-Storefront', name: 'Shopify Storefront connector', purpose: 'Sill calling the Shopify Storefront API for a connected store.', merchant_purpose: 'Sill reading your Shopify storefront through the connection you set up.', sample: 'Sill-Connector-Shopify-Storefront/1' },
    { agent_id: 'agent_sill_connector_woocommerce', token: 'Sill-Connector-WooCommerce', name: 'WooCommerce connector', purpose: 'Sill calling the WooCommerce REST API for a connected store.', merchant_purpose: 'Sill reading your WooCommerce store through the connection you set up.', sample: 'Sill-Connector-WooCommerce/1' },
    { agent_id: 'agent_sill_status_monitor', token: 'sill-status-monitor', name: 'Status monitor', purpose: 'The Sill status page checking that Sill services answer.', merchant_purpose: 'Sill checking that its own services are up.', sample: 'sill-status-monitor/1.0' },
    { agent_id: 'agent_sill_heartbeat_probe', token: 'SillHeartbeatProbe', name: 'Ingest heartbeat', purpose: 'The scheduled check that Sill ingestion is accepting events.', merchant_purpose: 'Sill checking that it is receiving visit data.', sample: 'SillHeartbeatProbe/1.0' },
    { agent_id: 'agent_sill_framework_watch', token: 'SillFrameworkWatch', name: 'Framework watch', purpose: 'Sill checking public framework documentation for changes.', merchant_purpose: 'Sill checking public documentation for changes.', sample: 'SillFrameworkWatch/1.0 (+https://sill.so)' },
    { agent_id: 'agent_sill_attack_paths_watch', token: 'SillAttackPathsWatch', name: 'Attack paths watch', purpose: 'Sill polling public threat-intel feeds (OWASP CRS, nuclei-templates, SecLists, CrowdSec) for new attack-shape path probes.', merchant_purpose: 'Sill keeping its blocklist up to date against the latest scanner path probes.', sample: 'SillAttackPathsWatch/1.0 (+https://sill.so)' },
    { agent_id: 'agent_sill_admin_ci_runs', token: 'sill-admin-ci-runs', name: 'Ops CI status', purpose: 'The Sill ops console reading its own build status from GitHub.', merchant_purpose: 'Sill reading its own build status.', sample: 'sill-admin-ci-runs/1.0' },
    { agent_id: 'agent_sill_admin_qa_escalate', token: 'sill-admin-qa-escalate', name: 'Ops QA escalation', purpose: 'The Sill ops console filing a QA issue on GitHub.', merchant_purpose: 'Sill filing an internal quality report.', sample: 'sill-admin-qa-escalate/1.0' },
    { agent_id: 'agent_sill_verified_crawler_refresh', token: 'Sill-VerifiedCrawler-Refresh', name: 'Verified-crawler list refresh', purpose: 'The Sill daily refresh job fetching an operator\'s published IP-range list (Google/Bing/Apple/DuckDuckGo/OpenAI/Perplexity/Anthropic).', merchant_purpose: 'Sill refreshing its list of verified crawler IPs so your firewall never blocks a real search bot.', sample: 'Sill-VerifiedCrawler-Refresh/1 (+https://sill.so/bot)' },
    { agent_id: 'agent_sill_cloud_ranges_refresh', token: 'Sill-CloudRanges-Refresh', name: 'Cloud IP ranges refresh', purpose: 'The Sill daily job fetching published cloud and privacy-relay IP ranges (Google Cloud, AWS EC2, Oracle, DigitalOcean, Cloudflare, Apple iCloud Private Relay).', merchant_purpose: 'Sill refreshing which addresses belong to cloud servers, CDNs and privacy relays, so blocking never hits real visitors.', sample: 'Sill-CloudRanges-Refresh/1 (+https://sill.so/bot)' },
    { agent_id: 'agent_sill_geo_db_refresh', token: 'Sill-GeoDbRefresh', name: 'IP location database refresh', purpose: 'The Sill daily job downloading the IP location and network-owner databases (MaxMind GeoLite2, DB-IP Lite).', merchant_purpose: 'Sill refreshing the data that shows where visitors come from and which network they use.', sample: 'Sill-GeoDbRefresh/1 (+https://sill.so/bot)' },
];
// Deliberately NOT here: `SillSmoke/` (packages/mandate/scripts/send-test-
// mandate.ts). That script registers its own KV identity WITH a signing key
// under `^SillSmoke/`; a bundled seed would win over it (bundled precedence,
// ADR-0229 M2) and detach the smoke key. Its traffic is already excluded
// by agent id (`AGENT_SILL_SMOKE`, @sill/audit).
const SILL_INTERNAL_SEEDS = SILL_INTERNAL_TOOLS.map((t) => ({
    agent_id: t.agent_id,
    category: 'internal',
    organization: 'Sill (internal)',
    display_name: `Sill (internal) · ${t.name}`,
    // Tokens are literal (letters, digits, `-`), so no escaping is needed.
    // Review N5 — anchored at the start: Sill's tools send their token first,
    // so a mid-string `Sill-…/` in someone else's UA never earns the label.
    user_agent_pattern: `^${t.token}/${V}`,
    visibility: 'internal',
    docs_url: 'https://sill.so',
    public_keys: [],
}));
export const BUNDLED_REGISTRY = [
    ...BUNDLED_PUBLIC_AND_FIXTURES,
    ...SILL_INTERNAL_SEEDS,
];
//# sourceMappingURL=agent-registry-data.js.map